import { ChevronRightIcon, EllipsisHorizontalIcon, HomeIcon } from '@heroicons/react/24/outline';
import { BreadcrumbItem, Breadcrumbs as BreadcrumbsWrapper } from '@heroui/react';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { isSupportedLocale } from '@/utils/locale';

import { BreadcrumbsContext } from '../../../contexts/BreadcrumbsContext';
import { BREADCRUMB_UPDATE_EVENT, getBreadcrumbTitle } from '../../../utils';
import {
  BreadcrumbLink,
  BreadcrumbsContainer,
  BreadcrumbText,
  Container,
  ExpandButton,
} from './Breadcrumbs.styled';

/* Pojedynczy segment ścieżki wyrenderowany jako link/tekst. */
type BreadcrumbSegment = {
  key: string;
  routeTo: string;
  name: string;
  isLast: boolean;
  isIdSegment: boolean;
  displayName: string;
  /* Dyskryminator unii — segment nigdy nie jest wielokropkiem. */
  isEllipsis?: false;
};

/* Zastępczy „…” rozwijający zwinięte segmenty. */
type BreadcrumbEllipsis = {
  key: string;
  isEllipsis: true;
};

type BreadcrumbVisibleItem = BreadcrumbSegment | BreadcrumbEllipsis;

export const Breadcrumbs: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const { breadcrumbs: registeredBreadcrumbs } = useContext(BreadcrumbsContext);
  const [, forceUpdate] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter out locale prefix from breadcrumb path segments
  const pathnames = location.pathname.split('/').filter((x) => x && !isSupportedLocale(x));

  // Listen for breadcrumb title updates from PageTitle
  useEffect(() => {
    const handleBreadcrumbUpdate = () => {
      forceUpdate({});
    };

    window.addEventListener(BREADCRUMB_UPDATE_EVENT, handleBreadcrumbUpdate);

    return () => {
      window.removeEventListener(BREADCRUMB_UPDATE_EVENT, handleBreadcrumbUpdate);
    };
  }, []);

  // Reset expansion state when location changes
  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);

  // Map URL segments to translation keys
  const segmentToTranslationKey: Record<string, string> = {
    // Routes
    hotel: 'routes.hotel',
    'general-information': 'routes.generalInformation',
    'business-card': 'routes.businessCards',
    amenities: 'routes.amenities',
    'good-practice': 'routes.goodPractice',
    phonebook: 'routes.phonebook',
    wifi: 'routes.wifi',
    regulations: 'routes.regulations',
    poi: 'routes.poi',
    'tv-chanel': 'routes.tvChannel',
    faq: 'routes.faq',
    'loyalty-program': 'routes.loyaltyProgram',
    'social-media': 'routes.socialMedia',
    'virtual-tour': 'routes.virtualTour',
    'link-to-reviews': 'routes.linkToReviews',
    'special-offer': 'routes.specialOffer',
    alerts: 'routes.alerts',
    events: 'routes.events',
    rental: 'routes.rental',
    surveys: 'routes.surveys',
    'take-to-room-service': 'routes.roomService',
    'order-products': 'routes.orderProducts',
    'products-services': 'routes.productsServices',
    'product-rental': 'routes.productRental',
    'ai-agent-chat': 'routes.aiAgentChat',
    marketing: 'routes.marketing',
    conferences: 'routes.conferences',
    spa: 'routes.spa',
    orders: 'routes.orders',
    reports: 'routes.reports',
    'my-devices': 'routes.myDevices',
    analytics: 'routes.analytics',
    'sales-analytics': 'routes.salesAnalytics',
    'ai-agent': 'routes.aiAgent',
    settings: 'routes.settings',
    help: 'routes.help',
    'my-profile': 'routes.myProfile',
    biling: 'routes.billing',
    'team-member': 'routes.teamMember',
    'support-service': 'routes.supportService',
    'faq-help': 'routes.faq',
    'centrum-powiadomien': 'routes.notificationsCenter',
    player: 'routes.player',
    'privacy-policy': 'routes.privacyPolicy',
    wiadomosci: 'routes.mails',
    // Common actions
    new: 'common.new',
    edit: 'common.edit',
    duplicate: 'common.duplicate',
    create: 'common.create',
    info: 'common.details',
    categories: 'common.categories',
    products: 'common.items',
    services: 'common.items',
  };

  const formatName = (str: string): string => str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const isId = useCallback((segment: string): boolean => {
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const shortIdPattern = /^[a-zA-Z0-9]{20,}$/;
    const numericPattern = /^\d+$/;

    return uuidPattern.test(segment) || shortIdPattern.test(segment) || numericPattern.test(segment);
  }, []);

  const getDisplayName = useCallback((segment: string, fullPath: string): string => {
    // If it's an ID, try to get saved title from localStorage first
    if (isId(segment)) {
      const savedTitle = getBreadcrumbTitle(segment);
      if (savedTitle) {
        // Shorten title if longer than 30 characters
        if (savedTitle.length > 30) {
          return `${savedTitle.slice(0, 30)}...`;
        }
        return savedTitle;
      }
      // Only show shortened ID as fallback if no title is saved
      return `#${segment.slice(0, 8)}...`;
    }

    // Check if we have a registered breadcrumb for this path
    const registered = registeredBreadcrumbs.find((b) => {
      // Match against normalized path (b.path has wildcards removed)
      if (b.path === fullPath) {
        return true;
      }

      // Match with parent path + path combination
      if (b.parentPath !== undefined && b.path !== undefined) {
        const fullRegisteredPath = `${b.parentPath}${b.path}`.replace('//', '/');
        if (fullRegisteredPath === fullPath) {
          return true;
        }
      }

      // Match against unique path (with wildcards)
      if (b.uniquePath === fullPath) {
        return true;
      }

      return false;
    });

    if (registered) {
      // Prefer translationKey for dynamic translation
      if (registered.translationKey) {
        return t(registered.translationKey);
      }
      // Fallback to static label
      if (registered.label) {
        return registered.label;
      }
    }

    // Fallback: try to translate using segment mapping
    if (segmentToTranslationKey[segment]) {
      const translated = t(segmentToTranslationKey[segment]);
      // Only return if translation was found (not the same as the key)
      if (translated !== segmentToTranslationKey[segment]) {
        return translated;
      }
    }

    return formatName(segment);
  }, [isId, registeredBreadcrumbs, t, segmentToTranslationKey]);

  // Memoize breadcrumb items to prevent unnecessary re-renders
  const breadcrumbItems = useMemo<BreadcrumbSegment[]>(() => pathnames.map((name, index) => {
    const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
    const isLast = index === pathnames.length - 1;
    const isIdSegment = isId(name);

    if (name === 'dashboard') return null;

    return {
      key: `${name}-${index}`,
      routeTo,
      name,
      isLast,
      isIdSegment,
      displayName: getDisplayName(name, routeTo),
    };
  }).filter((item): item is BreadcrumbSegment => item !== null), [pathnames, isId, getDisplayName]);

  // Determine which items to show based on expansion state
  const visibleItems = useMemo<BreadcrumbVisibleItem[]>(() => {
    if (isExpanded || breadcrumbItems.length <= 3) {
      return breadcrumbItems;
    }

    // Show first item, ellipsis, and last 2 items
    if (breadcrumbItems.length > 3) {
      return [
        breadcrumbItems[0],
        { isEllipsis: true, key: 'ellipsis' },
        ...breadcrumbItems.slice(-2),
      ];
    }

    return breadcrumbItems;
  }, [breadcrumbItems, isExpanded]);

  const separatorColor = theme.mode === 'dark' ? '#9ca3af' : '#6b7280';

  return (
    <BreadcrumbsContainer>
      <Container>
        <BreadcrumbsWrapper
          separator={(
            <ChevronRightIcon
              className="w-3 h-3"
              style={{ color: separatorColor }}
            />
          )}
          itemClasses={{
            separator: 'px-1',
            item: 'text-current',
          }}
        >
          <BreadcrumbItem>
            <BreadcrumbLink to="/" $isHome>
              <HomeIcon className="w-4 h-4" />
              <span>{t('breadcrumbs.dashboard')}</span>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {visibleItems.map((item) => {
            // Handle ellipsis item
            if (item.isEllipsis) {
              return (
                <BreadcrumbItem key={item.key}>
                  <ExpandButton onClick={() => setIsExpanded(true)}>
                    <EllipsisHorizontalIcon className="w-4 h-4" />
                  </ExpandButton>
                </BreadcrumbItem>
              );
            }

            const { key, routeTo, isLast, isIdSegment, displayName } = item;

            return (
              <BreadcrumbItem key={key} isDisabled={isIdSegment}>
                {isLast || isIdSegment ? (
                  <BreadcrumbText $isActive={isLast} $isDisabled={isIdSegment}>
                    {displayName}
                  </BreadcrumbText>
                ) : (
                  <BreadcrumbLink to={routeTo}>
                    {displayName}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbsWrapper>
      </Container>
    </BreadcrumbsContainer>
  );
};
